import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Pitch } from "@/components/Pitch";
import { choosePlanFit, centredOffset } from "@/lib/planFit";
import { GRID_FOR_COUNT, MAX_PLAN_ACTIVITIES, PLAN_ACTIVITY_LIMIT_MESSAGE } from "@/lib/planLayout";
import { PITCH_MODES, LEGACY_PITCH_MODE_ALIAS, getFieldPreset } from "@/constants/pitchObjects";
import { Logo } from "@/components/Logo";
import { Printer, X } from "lucide-react";

// ---------------------------------------------------------------
// Session Plan Generator — Passthrough architecture (V2.0)
// ---------------------------------------------------------------
// Architecture:
//     Activity Editor  →  saved activity/session data  →  Session Plan
//
// The Session Plan Generator is a PURE PASSTHROUGH. It renders the
// activity diagram using exactly the same <Pitch /> component and
// exactly the same activity data as the Activity Editor. It makes NO
// independent visual decisions about:
//   • player positions, sizes, colours, labels or label modes,
//   • equipment / shape / arrow / line / text / dimension appearance,
//   • pitch orientation, mode, field markings or field colour,
//   • layering / z-order,
//   • cropping, viewport zoom, or camera positioning.
//
// The only whole-diagram transformation the generator is allowed to
// apply is the existing uniform CSS scale-to-fit on the outer page
// (see the .plan-fit / .plan-content mechanism at the bottom of this
// file). That scale never alters individual objects or their relative
// proportions — every character, marker, arrow and dimension scales
// together as a single graphic.
//
// If two players overlap in the Activity Editor, they must overlap
// here. If a player is larger than another in the Activity Editor,
// that relationship must be preserved. Dense diagrams stay dense.

// Coerces a possibly-empty duration input into a non-negative number.
const durNum = (v) => {
  const n = Number(v);
  return isFinite(n) && n > 0 ? n : 0;
};
const activityTotal = (a) =>
  (durNum(a?.time) + durNum(a?.rest)) * durNum(a?.sets);

const humanLoad = (v) => {
  if (v === "low") return "Low";
  if (v === "medium") return "Medium";
  if (v === "high") return "High";
  return "—";
};

const humanDate = (d) => {
  if (!d) return "—";
  try {
    return new Date(d + "T00:00:00").toLocaleDateString(undefined, {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return d;
  }
};

const humanTime = (t) => {
  if (!t) return "—";
  return t;
};

// No-op prop bag for the Pitch component when used as a read-only diagram.
const NOOP = () => {};

// Width ÷ height of an activity's saved pitch view as the diagram draws
// it (viewport plus the 60-unit off-field padding Pitch.jsx adds on every
// side). Used to size the diagram box and the card's diagram column.
const OFF_PAD = 60; // must mirror Pitch.jsx's off-field padding
const pitchViewOf = (pitchMode) => {
  const rawMode = pitchMode || "vertical-full";
  const mode = LEGACY_PITCH_MODE_ALIAS[rawMode] || rawMode;
  const meta = PITCH_MODES[mode] || PITCH_MODES["vertical-full"];
  const viewW = meta.vertical ? meta.viewport.w : meta.viewport.h;
  const viewH = meta.vertical ? meta.viewport.h : meta.viewport.w;
  return { mode, viewW, viewH, aspect: (viewW + 2 * OFF_PAD) / (viewH + 2 * OFF_PAD) };
};

// Static read-only diagram powered by the shared <Pitch /> component.
// This is a PURE PASSTHROUGH — the generator makes no independent visual
// decisions. Every object, coordinate, size, colour, label, mode,
// orientation and z-order comes straight from the saved activity data
// and renders exactly as the Activity Editor would show it. The only
// whole-diagram transformation that occurs is the uniform CSS scale-
// to-fit on the outer plan page (see .plan-fit at the bottom of this
// file). See the architecture header at the top of this file.
const ActivityDiagram = ({ activity }) => {
  const { mode, viewW, viewH, aspect } = pitchViewOf(activity?.pitchMode);
  const fieldColor = activity?.fieldColor || "#86BC5A";
  const markingsColor = getFieldPreset(fieldColor).markings;

  // The Session Plan reproduces the Activity Editor's saved pitch view
  // exactly. The Pitch SVG draws the full canonical pitch (680 × 1050)
  // and lets its viewBox — derived from PITCH_MODES[mode].viewport —
  // crop to the visible viewport. However, `preserveAspectRatio="meet"`
  // (which the shared Pitch component uses so the pitch never
  // distorts) letterboxes the viewBox inside the SVG DOM element,
  // and content OUTSIDE the viewBox that falls in the letterbox area
  // is NOT clipped by the browser — it renders on top of the empty
  // letterbox space. Concretely: a "Vertical · Attacking Third"
  // activity (viewport 680×350) placed inside a portrait wrapper would
  // spill the middle and defensive thirds into the letterbox, showing
  // the whole pitch instead of just the attacking third.
  //
  // The fix is to constrain the diagram wrapper's aspect ratio to the
  // viewport's aspect (including the off-field padding Pitch uses for
  // off-field objects). Then `meet` fills the wrapper exactly, no
  // letterbox and no spillover — the coach's saved pitch view is
  // reproduced faithfully. The card footprint is unchanged because
  // the wrapper still centers inside the card's fixed diagram column.
  //
  // NP-3C-7: the page fit (fitDiagrams, below) gives the box the largest
  // size of this aspect ratio that fits the card's diagram column — its
  // full width, or its full height for a pitch wider than the space is
  // tall. The column is size-contained, so the box never sets the card's
  // height, and it no longer falls back to the SVG's ~80 mm default width
  // (NP-3C-3). The diagram itself is untouched: same viewBox, same
  // objects, uniformly scaled.
  const aspectRatio = `${viewW + 2 * OFF_PAD} / ${viewH + 2 * OFF_PAD}`;

  return (
    <div
      className="w-full h-full flex items-center justify-center overflow-hidden"
      data-testid="plan-activity-diagram"
      data-pitch-mode={mode}
      data-view-w={viewW}
      data-view-h={viewH}
    >
      <div
        className="rounded-md overflow-hidden border border-slate-300 max-w-full max-h-full pointer-events-none select-none"
        style={{ background: fieldColor, aspectRatio }}
        data-pitch-aspect={aspect.toFixed(4)}
      >
        <Pitch
          mode={mode}
          fieldColor={fieldColor}
          markingsColor={markingsColor}
          objects={activity?.objects || []}
          arrows={activity?.arrows || []}
          shapes={activity?.shapes || []}
          texts={activity?.texts || []}
          lines={activity?.lines || []}
          activeTool={null}
          selectedId={null}
          selectedShapeId={null}
          selectedArrowId={null}
          selectedTextId={null}
          selectedLineId={null}
          multiSelection={null}
          drawStyle={{ color: "#0F172A", dashed: false }}
          onSelect={NOOP}
          onSelectShape={NOOP}
          onSelectArrow={NOOP}
          onSelectText={NOOP}
          onSelectLine={NOOP}
          onToggleSelection={NOOP}
          onMoveObject={NOOP}
          onMoveShape={NOOP}
          onMoveText={NOOP}
          onMoveArrow={NOOP}
          onMoveArrowEndpoint={NOOP}
          onCurveArrow={NOOP}
          onMoveLine={NOOP}
          onMoveLineEndpoint={NOOP}
          onResizeShape={NOOP}
          onMoveShapeDim={NOOP}
          onDragStart={NOOP}
          onDuplicateObject={NOOP}
          onDuplicateShape={NOOP}
          onDuplicateText={NOOP}
          onDuplicateArrow={NOOP}
          onDuplicateLine={NOOP}
          onAddArrow={NOOP}
          onAddShape={NOOP}
          onAddLine={NOOP}
          onPitchClick={NOOP}
          onMarqueeSelect={NOOP}
          onMultiDelta={NOOP}
          onResizeObject={NOOP}
          onRotateObject={NOOP}
          showDimensions={activity?.showDimensions !== false}
        />
      </div>
    </div>
  );
};

const CoachingPointGroup = ({ label, accent, items }) => {
  if (!items || items.length === 0) return null;
  return (
    <div className="flex flex-col gap-0.5 min-w-0">
      <span
        className={
          "text-[7pt] font-bold uppercase tracking-wider " +
          (accent === "offensive" ? "text-emerald-700" : "text-sky-700")
        }
      >
        {label}
      </span>
      <ul className="list-disc pl-3.5 text-[8pt] text-slate-800 leading-snug break-words">
        {items.map((p) => (
          <li key={p.id}>{p.text}</li>
        ))}
      </ul>
    </div>
  );
};

// ================================================================
// LAYER 3: PAGE COMPOSITION ENGINE
// ================================================================
// ================================================================
// LAYER 3: PAGE COMPOSITION ENGINE — UNIFORM ACTIVITY CARDS
// ================================================================
// Cards are placed by a fixed rule per activity count and orientation
// (lib/planLayout). Complexity, player count, equipment count, arrow
// count, coaching-point count and rules length never affect the card
// grid: cards in the same row always share one height, and every card
// in a row has the same width. The Activity Editor owns what each
// activity looks like; this composition engine only decides where each
// card sits on the page. There is no per-activity metric and no
// adaptive fr weight. (NP-3C-7: a 1–2 activity card may grow beyond its
// intentional height when its text needs more room, rather than letting
// the text run out of the card.)
//
// Card / diagram layout constants:
//   • DIAGRAM_FLEX_PCT — global fraction of card width used by the
//     diagram column of a side card. The remainder is the info column.
//     Same for every card, every session, every N.
//   • GRID_FOR_COUNT   — deterministic grid for N activities (1–6),
//     defined in lib/planLayout (NP-3C-7): cards per row, the columns
//     each card spans and the card style. Rows are always equal height;
//     cards in a shorter row are wider (portrait 5 = 2 + 2 + 1, the 5th
//     spanning the full width; landscape 5 = 3 + 2).
const DIAGRAM_FLEX_PCT = 68;

// NP-3C-7 card layouts:
//   • "side"    — diagram left, text right (every layout except portrait 3).
//   • "stacked" — diagram above the text, for tall narrow cards (portrait 3).
// In both, the diagram column is size-contained: it takes its size from
// the card, never the other way round, so a tall pitch can no longer make
// its card (and so the page) taller. The page fit sizes the diagram inside
// it (fitDiagrams). `span` is the number of grid columns the card spans
// (see lib/planLayout).
const STACKED_DIAGRAM_FLOOR = 0.6; // stacked: the diagram keeps ≥ 60% of its full-width height
const ActivityCard = ({ a, i, totalMin, testIdIndex, layout = "side", span = 1 }) => {
  const offensive = (a.coachingPoints || []).filter((p) => p.type === "offensive");
  const defensive = (a.coachingPoints || []).filter((p) => p.type === "defensive");
  const stacked = layout === "stacked";
  return (
    <article
      className="plan-activity flex flex-col gap-1 rounded border border-slate-300 bg-white p-1.5 h-full min-h-0 shadow-[0_0_0_0.5pt_rgba(15,23,42,0.04)]"
      data-testid={`plan-activity-${testIdIndex}`}
      data-diagram-flex-pct={DIAGRAM_FLEX_PCT}
      data-card-layout={layout}
      style={span > 1 ? { gridColumn: `span ${span}` } : undefined}
    >
      <header className="flex items-start justify-between gap-2 pb-1 border-b border-slate-200">
        <div className="flex items-start gap-1.5 min-w-0">
          <span
            className="shrink-0 w-5 h-5 rounded-full bg-slate-900 text-white text-[9pt] font-bold flex items-center justify-center tabular-nums"
            style={{ fontFamily: "Manrope, sans-serif" }}
          >
            {i + 1}
          </span>
          <h2
            className="text-[10.5pt] font-bold text-slate-900 leading-tight break-words"
            style={{ fontFamily: "Manrope, sans-serif" }}
          >
            {(a.name || "").trim() || `Activity ${i + 1}`}
          </h2>
        </div>
        <span className="shrink-0 text-[9pt] font-bold text-slate-800 tabular-nums">
          {totalMin} min
        </span>
      </header>

      {/* Side cards: DIAGRAM_FLEX_PCT of the card width is the diagram
          column, the remainder is the info column. The diagram is passed
          to <Pitch /> verbatim. NP-3C-7: when the pitch view is wider
          than the column's height allows, the page fit narrows the
          column to the diagram's width so the spare width goes to the
          text (fitDiagrams). Card dimensions never depend on the
          activity's content.
          Stacked cards: the diagram fills the height the text leaves but
          keeps at least STACKED_DIAGRAM_FLOOR of its full-width height
          (set by fitDiagrams); past that the card grows and the page fit
          takes over. */}
      <div
        className={stacked ? "flex flex-col gap-1 min-h-0 flex-1" : "flex gap-2 items-stretch min-h-0 flex-1"}
      >
        <div
          className="flex items-stretch"
          style={
            stacked
              ? { flex: "1 1 0", minWidth: 0, minHeight: 0, contain: "size" }
              : { flex: `0 0 ${DIAGRAM_FLEX_PCT}%`, minWidth: 0, contain: "size" }
          }
          data-plan-diagram-col={stacked ? "stacked" : "side"}
        >
          <ActivityDiagram activity={a} />
        </div>

        <div
          className={stacked ? "flex flex-col gap-1 min-w-0" : "flex-1 flex flex-col gap-1 min-w-0"}
          style={stacked ? { flex: "0 0 auto" } : undefined}
        >
          {/* Compact metadata strip. Pitch view/type is intentionally
              omitted here — the diagram itself already renders the
              activity's pitch configuration, and coaches don't need
              the label duplicated on the printed session plan. Pitch
              mode remains part of the activity data model and the
              Activity Editor UI. */}
          <div className="grid grid-cols-2 gap-1 text-[7.5pt]">
            {[
              ["Time", `${durNum(a.time) || 0}m`],
              ["Sets", durNum(a.sets) || 0],
              ["Rest", `${durNum(a.rest) || 0}m`],
            ].map(([label, value]) => (
              <div key={label} className="flex flex-col rounded bg-slate-50 px-1 py-0.5 min-w-0">
                <span className="text-[6.5pt] tracking-wider uppercase font-bold text-slate-400">
                  {label}
                </span>
                <span className="font-semibold text-slate-800 leading-tight break-words">
                  {value}
                </span>
              </div>
            ))}
          </div>

          {/* Information hierarchy (V2.0): every card presents its text
              sections in exactly this order — Key Coaching Points →
              Rules → Rotations. Each section is conditional so an
              empty section produces no blank placeholder, but the
              relative order between populated sections is fixed. */}
          {(offensive.length > 0 || defensive.length > 0) && (
            <div
              className="flex flex-col gap-0.5 min-w-0"
              data-testid={`plan-activity-coaching-${testIdIndex}`}
            >
              <span className="text-[6.5pt] tracking-wider uppercase font-bold text-slate-400">
                Key Coaching Points
              </span>
              <div className="flex flex-col gap-1">
                <CoachingPointGroup label="Offensive" accent="offensive" items={offensive} />
                <CoachingPointGroup label="Defensive" accent="defensive" items={defensive} />
              </div>
            </div>
          )}

          {a.rules ? (
            <div className="flex flex-col gap-0 min-w-0">
              <span className="text-[6.5pt] tracking-wider uppercase font-bold text-slate-400">
                Rules
              </span>
              <p
                className="text-[8pt] text-slate-800 leading-snug whitespace-pre-line break-words"
                data-testid={`plan-activity-rules-${testIdIndex}`}
              >
                {a.rules}
              </p>
            </div>
          ) : null}

          {Array.isArray(a.rotations) && a.rotations.length > 0 && (
            <div
              className="flex flex-col gap-0.5 min-w-0"
              data-testid={`plan-activity-rotations-${testIdIndex}`}
            >
              <span className="text-[6.5pt] tracking-wider uppercase font-bold text-slate-400">
                Rotations
              </span>
              {(() => {
                // Group rotations by optional `afterSet`:
                //   • Unscheduled (afterSet == null) render first as a
                //     flat list — matches the pre-existing style.
                //   • Scheduled rotations are grouped by their set
                //     number and rendered in ascending order beneath
                //     a compact "After Set N:" subheading. Order
                //     within a group is preserved (stable partition).
                const unscheduled = [];
                const scheduledMap = new Map();
                a.rotations.forEach((r) => {
                  const set =
                    typeof r.afterSet === "number" && Number.isFinite(r.afterSet) && r.afterSet >= 1
                      ? r.afterSet
                      : null;
                  if (set == null) {
                    unscheduled.push(r);
                  } else {
                    if (!scheduledMap.has(set)) scheduledMap.set(set, []);
                    scheduledMap.get(set).push(r);
                  }
                });
                const scheduledSets = Array.from(scheduledMap.keys()).sort(
                  (x, y) => x - y
                );

                const renderRow = (r) => (
                  <li
                    key={r.id}
                    className="flex items-center gap-1 text-[7.5pt] text-slate-800 leading-tight"
                    data-testid={`plan-rotation-${r.id}`}
                  >
                    <span className="flex-1 min-w-0 break-words font-semibold">
                      {(r.from || "").trim() || "—"}
                    </span>
                    <span aria-hidden className="shrink-0 text-slate-500 text-[8pt]">↔</span>
                    <span className="flex-1 min-w-0 break-words font-semibold">
                      {(r.to || "").trim() || "—"}
                    </span>
                  </li>
                );

                return (
                  <div className="flex flex-col gap-1">
                    {unscheduled.length > 0 && (
                      <ul
                        className="flex flex-col gap-0.5"
                        data-testid={`plan-rotations-unscheduled-${testIdIndex}`}
                      >
                        {unscheduled.map(renderRow)}
                      </ul>
                    )}
                    {scheduledSets.map((setNum) => (
                      <div
                        key={setNum}
                        className="flex flex-col gap-0.5"
                        data-testid={`plan-rotation-group-${testIdIndex}-${setNum}`}
                      >
                        <span
                          className="text-[7pt] font-semibold text-slate-500 leading-tight"
                          data-testid={`plan-rotation-group-heading-${testIdIndex}-${setNum}`}
                        >
                          After Set {setNum}:
                        </span>
                        <ul className="flex flex-col gap-0.5 pl-1">
                          {scheduledMap.get(setNum).map(renderRow)}
                        </ul>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      </div>
    </article>
  );
};

// Uniform-card composition engine. Cards in the same row always share
// one height, and card dimensions never depend on an activity's content.
// Supports 1–6 activities (lib/planLayout); anything else renders nothing
// here — the preview shows the activity-limit notice instead.
const ActivitiesSection = ({ activities, totals, orientation = "portrait" }) => {
  const n = activities.length;
  if (n === 0) return null;

  const grid = GRID_FOR_COUNT(n, orientation);
  if (!grid) return null;
  const { cols, rows, tracks, spans, layout, card } = grid;

  // ---- Controlled-height layout for exactly 1 activity ----
  // (Applies to both orientations — one intentional-height full-width
  // card, no stretch.)
  //
  // Portrait: two-activity sessions also stack vertically with a
  // controlled height. Landscape puts them side-by-side using the
  // stretched grid (below) so each card gets the fuller page width.
  //
  // NP-3C-7: the intentional height is a MINIMUM. A card whose text needs
  // more room grows (and the page fit shrinks the page if needed) instead
  // of its text running out of the card.
  const useControlledHeight =
    n === 1 || (n === 2 && orientation === "portrait");
  if (useControlledHeight) {
    const cardHeight =
      n === 1
        ? orientation === "landscape" ? "115mm" : "130mm"
        : "88mm"; // N=2 portrait only
    return (
      <section
        className="mt-1.5 flex flex-col gap-2"
        data-testid="plan-activities"
        data-grid-cols={cols}
        data-grid-rows={rows}
        data-grid-layout={layout}
        data-activity-count={n}
        data-layout={n === 1 ? "single" : "stacked"}
        data-orientation={orientation}
      >
        {activities.map((a, i) => (
          <div key={i} className="min-h-0" style={{ minHeight: cardHeight, display: "grid", gridTemplateRows: `minmax(${cardHeight}, auto)` }}>
            <ActivityCard
              a={a}
              i={i}
              totalMin={totals[i]}
              testIdIndex={i}
            />
          </div>
        ))}
      </section>
    );
  }

  // ---- Existing uniform stretched grid for 3+ activities; also used
  // for N=2 in landscape (side-by-side). Equal-height rows; `tracks`
  // equal columns, each card spanning spans[i] of them. ----
  return (
    <section
      className="mt-1.5 grid gap-2 flex-1 min-h-0"
      style={{
        gridTemplateColumns: `repeat(${tracks}, minmax(0, 1fr))`,
        gridTemplateRows: `repeat(${rows}, minmax(0, 1fr))`,
      }}
      data-testid="plan-activities"
      data-grid-cols={cols}
      data-grid-rows={rows}
      data-grid-layout={layout}
      data-activity-count={n}
      data-orientation={orientation}
    >
      {activities.map((a, i) => (
        <ActivityCard
          key={i}
          a={a}
          i={i}
          totalMin={totals[i]}
          testIdIndex={i}
          layout={card}
          span={spans[i]}
        />
      ))}
    </section>
  );
};

// NP-3C-7 — diagram sizing, run by the page fit on the untransformed page.
// For every card:
//   • the diagram box gets the largest size of its pitch view's aspect
//     ratio that fits the diagram column (the column never grows for it);
//   • side cards: when the pitch view is wider than the column's height
//     allows, the column is narrowed to the diagram so the spare width
//     goes to the text column;
//   • stacked cards: the diagram column keeps at least
//     STACKED_DIAGRAM_FLOOR of the diagram's full-width height.
// A few passes, because the text reflow can change a card body's height;
// styles are only written when they change, and `reset` starts every page
// fit from the same state so a re-fit gives the same result. The fit's
// trial layouts pass `sizeBoxes: false`: a box's size never changes the
// page height (its column is size-contained), so only the columns matter.
const fitDiagrams = (root, { reset = false, sizeBoxes = true } = {}) => {
  const columns = [...root.querySelectorAll("[data-plan-diagram-col]")];
  if (columns.length === 0) return;
  const full = `${DIAGRAM_FLEX_PCT}%`;
  const set = (el, prop, value) => {
    if (el.style[prop] === value) return false;
    el.style[prop] = value;
    return true;
  };
  if (reset) {
    columns.forEach((c) => {
      if (c.dataset.planDiagramCol === "side") c.style.flexBasis = full;
      else c.style.minHeight = "0px";
    });
  }
  for (let pass = 0; pass < 3; pass++) {
    const boxes = columns.map((c) => ({ body: c.parentElement.getBoundingClientRect(), col: c.getBoundingClientRect() }));
    let changed = false;
    columns.forEach((c, k) => {
      const box = c.querySelector("[data-pitch-aspect]");
      const aspect = Number(box.dataset.pitchAspect);
      const { body, col } = boxes[k];
      let colWidth = col.width;
      if (c.dataset.planDiagramCol === "side") {
        const fullWidth = (body.width * DIAGRAM_FLEX_PCT) / 100;
        const needed = body.height * aspect + 2; // + the box border
        colWidth = needed < fullWidth ? needed : fullWidth;
        changed = set(c, "flexBasis", needed < fullWidth ? `${needed.toFixed(1)}px` : full) || changed;
      } else {
        changed = set(c, "minHeight", `${((STACKED_DIAGRAM_FLOOR * body.width) / aspect).toFixed(1)}px`) || changed;
      }
      if (sizeBoxes) {
        const width = Math.max(0, Math.min(colWidth, col.height * aspect));
        changed = set(box, "width", `${Math.floor(width * 10) / 10}px`) || changed;
      }
    });
    if (!changed) break;
  }
};

export const SessionPlanPreview = ({
  open,
  onClose,
  sessionSnapshot, // { name, date, startTime, location, objective, load, offensivePrinciples, defensivePrinciples, activities }
}) => {
  const activities = sessionSnapshot?.activities || [];
  // NP-3C-7: a plan supports up to MAX_PLAN_ACTIVITIES activities. Beyond
  // that no plan is rendered or printed — the preview shows a notice.
  const overLimit = activities.length > MAX_PLAN_ACTIVITIES;
  const totals = useMemo(() => activities.map(activityTotal), [activities]);
  const sessionTotal = totals.reduce((s, v) => s + v, 0);

  // Document-level orientation setting. Portrait is the default so
  // existing users see no change; landscape uses the wider A4 page and
  // adapts the activity grid for small session counts (see GRID_FOR_COUNT).
  // The setting lives here — it never touches activity/session data.
  const [orientation, setOrientation] = useState("portrait");
  const isLandscape = orientation === "landscape";
  const pageWidthMm = isLandscape ? 297 : 210;
  const pageHeightMm = isLandscape ? 210 : 297;

  // Dynamic scale-to-fit safety net. The .plan-fit wrapper occupies the
  // exact usable A4 inner height. If .plan-content naturally overflows we
  // apply a uniform CSS transform so every character, diagram and gap
  // shrinks together — no truncation, no clipping, aspect ratios and
  // player initials preserved.
  //
  // NP-3B: before scaling, the content may be laid out wider (the width
  // the shrink frees up) so text reflows and the page is scaled less —
  // see lib/planFit. Any width the scaled page still cannot use is split
  // equally left and right. It remains ONE uniform scale of the page.
  const fitRef = useRef(null);
  const contentRef = useRef(null);
  // NP-3C-7: whether this opening's delayed font-readiness re-fit has run.
  // It runs once per opening of the plan, not on later orientation changes.
  const openingRefitDoneRef = useRef(false);

  useLayoutEffect(() => {
    if (!open) {
      openingRefitDoneRef.current = false; // the next opening re-fits again
      return;
    }
    let settledSize = null; // content box size after our own last fit
    const measure = () => {
      const fit = fitRef.current;
      const content = contentRef.current;
      if (!fit || !content) return;
      const started = performance.now();
      content.style.transform = "";
      content.style.width = "100%";
      content.style.minHeight = "100%";
      content.style.marginLeft = "0px";
      const usable = fit.clientHeight;
      const width = fit.clientWidth;
      fitDiagrams(content, { reset: true, sizeBoxes: false });
      const natural = content.scrollHeight;
      let tries = 0;
      const { scale: chosenScale, widthFactor, baseScale } = choosePlanFit({
        usable,
        natural,
        heightAtWidthFactor: (k) => {
          tries += 1;
          content.style.width = `${width / k}px`;
          content.style.minHeight = `${usable / k}px`;
          fitDiagrams(content, { sizeBoxes: false });
          return content.scrollHeight;
        },
      });
      let scale = chosenScale;
      if (scale < 1) {
        content.style.width = widthFactor === 1 ? "100%" : `${width / widthFactor}px`;
        content.style.minHeight = `${usable / scale}px`;
      } else {
        content.style.width = "100%";
        content.style.minHeight = "100%";
      }
      // NP-3C-7: lay the diagram columns out for the final page and
      // re-check it. They can settle slightly differently from the trial
      // the fit chose; the page must still never overflow.
      for (let pass = 0; pass < 3; pass++) {
        fitDiagrams(content);
        const settledHeight = content.scrollHeight;
        if (settledHeight * scale <= usable + 0.5) break;
        scale = usable / settledHeight;
        content.style.minHeight = `${usable / scale}px`;
      }
      if (scale < 1) {
        content.style.marginLeft = `${centredOffset({ width, widthFactor, scale })}px`;
        content.style.transform = `scale(${scale})`;
        content.style.transformOrigin = "top left";
      }
      settledSize = { w: content.offsetWidth, h: content.offsetHeight };
      // Test instrumentation only (NP-3A/3B), never affects layout: the
      // scale applied (1 = no shrink), the plain height-only scale, how
      // long this fit took (ms) and (NP-3C-7) how many layout tries the
      // fit search made.
      content.setAttribute("data-fit-scale", scale.toFixed(4));
      content.setAttribute("data-fit-base-scale", baseScale.toFixed(4));
      content.setAttribute("data-fit-ms", String(Math.round(performance.now() - started)));
      content.setAttribute("data-fit-tries", String(tries));
    };
    // At most one fit per animation frame.
    let frame = 0;
    const schedule = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        measure();
      });
    };
    measure();
    // Content changes (fonts, images) re-fit; the size change caused by
    // our own fit is ignored.
    const ro = new ResizeObserver((entries) => {
      const box = entries[0] && entries[0].target;
      if (box && settledSize && Math.abs(box.offsetWidth - settledSize.w) < 1 && Math.abs(box.offsetHeight - settledSize.h) < 1) return;
      schedule();
    });
    if (contentRef.current) ro.observe(contentRef.current);
    window.addEventListener("resize", schedule);
    // Fonts may not be ready on first paint — re-measure once they settle.
    // NP-3C-7: only when the plan has just been opened. An orientation
    // change fits the page once (above); fonts are already loaded by then.
    // If the effect re-runs before the delayed re-fit fires (e.g. an
    // orientation change within 200 ms of opening), it is re-armed.
    let t = 0;
    if (!openingRefitDoneRef.current) {
      t = setTimeout(() => {
        openingRefitDoneRef.current = true;
        // Test instrumentation only: the opening's delayed re-fit ran.
        if (contentRef.current) contentRef.current.setAttribute("data-fit-opening-refit", "1");
        schedule();
      }, 200);
    }
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", schedule);
      clearTimeout(t);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [open, sessionSnapshot, orientation]);

  if (!open) return null;

  // Rendered straight into <body> so printing can isolate the plan from
  // the rest of the app (see the print CSS below).
  return createPortal(
    <div
      className="fixed inset-0 z-50 bg-slate-100 flex flex-col plan-overlay"
      data-testid="session-plan-preview"
    >
      {/* Non-printing action bar */}
      <div className="plan-toolbar shrink-0 flex items-center justify-between px-6 py-3 bg-white border-b border-slate-200">
        <div className="flex items-center gap-2">
          <span className="text-[10px] tracking-[0.25em] uppercase font-bold text-slate-400">
            Session Plan Preview
          </span>
          <span
            className="text-sm font-semibold text-slate-900 max-w-[420px] truncate"
            style={{ fontFamily: "Manrope, sans-serif" }}
          >
            · {sessionSnapshot?.name || "Untitled Session"}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {!overLimit && (<>
          {/* Orientation selector — segmented control. Portrait is default. */}
          <div
            className="flex items-center gap-0.5 p-0.5 rounded-md bg-slate-100"
            data-testid="plan-orientation-toggle"
            role="group"
            aria-label="Page orientation"
          >
            {[
              { key: "portrait", label: "Portrait" },
              { key: "landscape", label: "Landscape" },
            ].map((opt) => (
              <button
                key={opt.key}
                type="button"
                onClick={() => setOrientation(opt.key)}
                data-testid={`plan-orientation-${opt.key}`}
                aria-pressed={orientation === opt.key}
                className={
                  "px-3 py-1 text-[10px] font-semibold uppercase tracking-wider rounded transition-colors " +
                  (orientation === opt.key
                    ? "bg-white text-slate-900 shadow-sm"
                    : "text-slate-500 hover:text-slate-900")
                }
              >
                {opt.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => window.print()}
            data-testid="plan-print-btn"
            className="h-9 px-4 flex items-center gap-2 rounded-md bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 transition-colors"
          >
            <Printer className="w-4 h-4" />
            Print / Save as PDF
          </button>
          </>)}
          <button
            type="button"
            onClick={onClose}
            data-testid="plan-close-btn"
            className="h-9 w-9 flex items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 transition-colors"
            aria-label="Close preview"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {overLimit ? (
        <div
          className="flex-1 flex items-center justify-center p-6"
          data-testid="plan-activity-limit"
          role="status"
        >
          <p className="text-sm font-semibold text-slate-700">{PLAN_ACTIVITY_LIMIT_MESSAGE}</p>
        </div>
      ) : (
      /* Exactly-A4 page. Height is fixed so scale-to-fit always targets
         the same usable area. */
      <div className="flex-1 overflow-y-auto p-6 flex justify-center plan-scroll">
        <div
          className="plan-page bg-white shadow-md text-slate-900"
          data-testid="plan-page"
          data-orientation={orientation}
          style={{
            width: `${pageWidthMm}mm`,
            height: `${pageHeightMm}mm`,
            padding: "8mm",
            boxSizing: "border-box",
            fontFamily: "IBM Plex Sans, sans-serif",
            overflow: "hidden",
          }}
        >
          <div
            ref={fitRef}
            className="plan-fit"
            style={{
              width: "100%",
              height: "100%",
              overflow: "hidden",
              position: "relative",
            }}
          >
            <div
              ref={contentRef}
              className="plan-content"
              style={{ width: "100%", minHeight: "100%", display: "flex", flexDirection: "column" }}
            >
              {/* HEADER */}
              <header className="flex items-start justify-between gap-4 pb-1 border-b-2 border-slate-900">
                <div className="flex flex-col gap-0 min-w-0 flex-1">
                  <Logo
                    variant="wordmark"
                    height={13}
                    className="mb-0.5"
                    data-testid="plan-brand-logo"
                    style={{ maxWidth: "62mm" }}
                  />
                  <h1
                    className="text-[15pt] leading-tight font-black text-slate-900"
                    style={{ fontFamily: "Manrope, sans-serif" }}
                    data-testid="plan-session-name"
                  >
                    {sessionSnapshot?.name || "Untitled Session"}
                  </h1>
                  {sessionSnapshot?.objective ? (
                    <p className="text-[8pt] text-slate-700 leading-snug">
                      {sessionSnapshot.objective}
                    </p>
                  ) : null}
                </div>
                <div className="shrink-0 grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5 text-[8pt] text-right">
                  <span className="text-slate-400 font-semibold uppercase tracking-wider">Date</span>
                  <span className="font-semibold text-slate-900" data-testid="plan-session-date">
                    {humanDate(sessionSnapshot?.date)}
                  </span>
                  <span className="text-slate-400 font-semibold uppercase tracking-wider">Time</span>
                  <span className="font-semibold text-slate-900" data-testid="plan-session-time">
                    {humanTime(sessionSnapshot?.startTime)}
                  </span>
                  <span className="text-slate-400 font-semibold uppercase tracking-wider">Location</span>
                  <span className="font-semibold text-slate-900" data-testid="plan-session-location">
                    {sessionSnapshot?.location || "—"}
                  </span>
                  <span className="text-slate-400 font-semibold uppercase tracking-wider">Load</span>
                  <span className="font-semibold text-slate-900" data-testid="plan-session-load">
                    {humanLoad(sessionSnapshot?.load)}
                  </span>
                  <span className="text-slate-400 font-semibold uppercase tracking-wider">Total</span>
                  <span
                    className="font-bold text-slate-900 tabular-nums"
                    data-testid="plan-session-total"
                  >
                    {sessionTotal} min
                  </span>
                </div>
              </header>

              {/* OVERVIEW + PRINCIPLES band */}
              {(activities.length > 0 ||
                sessionSnapshot?.offensivePrinciples?.length > 0 ||
                sessionSnapshot?.defensivePrinciples?.length > 0) && (
                <section className="mt-1.5 grid grid-cols-3 gap-2" data-testid="plan-overview-band">
                  {activities.length > 0 && (
                    <div data-testid="plan-overview">
                      <span className="text-[7pt] tracking-[0.22em] uppercase font-bold text-slate-500 mb-0.5 block">
                        Overview
                      </span>
                      <table className="w-full text-[8pt] border border-slate-200">
                        <thead className="bg-slate-100">
                          <tr>
                            <th className="w-5 py-0.5 px-1 text-left font-semibold text-slate-600">#</th>
                            <th className="py-0.5 px-1 text-left font-semibold text-slate-600">Activity</th>
                            <th className="w-12 py-0.5 px-1 text-right font-semibold text-slate-600">Min</th>
                          </tr>
                        </thead>
                        <tbody>
                          {activities.map((a, i) => (
                            <tr
                              key={i}
                              className="border-t border-slate-200"
                              data-testid={`plan-overview-row-${i}`}
                            >
                              <td className="py-0.5 px-1 tabular-nums font-bold text-slate-700">{i + 1}</td>
                              <td className="py-0.5 px-1 text-slate-800">
                                {(a.name || "").trim() || `Activity ${i + 1}`}
                              </td>
                              <td className="py-0.5 px-1 text-right tabular-nums font-semibold text-slate-800">
                                {totals[i]}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  <div className="rounded border border-emerald-200 bg-emerald-50/40 px-2 py-1">
                    <span className="text-[7pt] tracking-[0.22em] uppercase font-bold text-emerald-700">
                      Offensive Principles
                    </span>
                    {sessionSnapshot?.offensivePrinciples?.length ? (
                      <ul className="list-disc pl-3.5 mt-0.5 text-[8pt] text-slate-800 leading-snug">
                        {sessionSnapshot.offensivePrinciples.map((p, i) => (
                          <li key={i}>{p}</li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-[8pt] text-slate-400 mt-0.5">—</p>
                    )}
                  </div>
                  <div className="rounded border border-sky-200 bg-sky-50/40 px-2 py-1">
                    <span className="text-[7pt] tracking-[0.22em] uppercase font-bold text-sky-700">
                      Defensive Principles
                    </span>
                    {sessionSnapshot?.defensivePrinciples?.length ? (
                      <ul className="list-disc pl-3.5 mt-0.5 text-[8pt] text-slate-800 leading-snug">
                        {sessionSnapshot.defensivePrinciples.map((p, i) => (
                          <li key={i}>{p}</li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-[8pt] text-slate-400 mt-0.5">—</p>
                    )}
                  </div>
                </section>
              )}

              {/* ADAPTIVE ACTIVITY GRID */}
              <ActivitiesSection activities={activities} totals={totals} orientation={orientation} />

              <footer className="mt-1.5 pt-1 border-t border-slate-200 flex items-center justify-between text-[7pt] text-slate-400 uppercase tracking-wider">
                <span>The Training Ground · Session Plan</span>
                <span>{activities.length} activit{activities.length === 1 ? "y" : "ies"} · {sessionTotal} min total</span>
              </footer>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Print CSS — force exactly ONE A4 page. */}
      <style>{`
        @media print {
          @page { size: A4 ${orientation}; margin: 0; }
          html, body { background: #fff !important; margin: 0 !important; padding: 0 !important; height: auto !important; overflow: visible !important; }
          /* Print ONLY the plan: the app, toasts and dialogs never print,
             and nothing clips the page (an oversized plan would show as a
             second page instead of being silently cut). */
          body > *:not(.plan-overlay) { display: none !important; }
          .plan-overlay { position: static !important; background: #fff !important; height: auto !important; }
          .plan-toolbar { display: none !important; }
          .plan-scroll { overflow: visible !important; padding: 0 !important; display: block !important; }
          .plan-page {
            width: ${pageWidthMm}mm !important;
            height: ${pageHeightMm}mm !important;
            margin: 0 !important;
            box-shadow: none !important;
            border-radius: 0 !important;
            page-break-after: avoid;
            page-break-inside: avoid;
            break-after: avoid;
            break-inside: avoid;
          }
          .plan-activity { break-inside: avoid; page-break-inside: avoid; }
          /* Diagrams print as sharp vector graphics. The pitch's subtle
             on-screen drop shadow is a CSS filter, and filters make the
             print engine rasterise each diagram at 300 dpi (blurring
             player labels and markings). Print only; screen unchanged. */
          .plan-page [data-testid="pitch-canvas"] { filter: none !important; }
          * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
        }
      `}</style>
    </div>,
    document.body
  );
};
